import React, { useState, useRef, useEffect } from "react";
import "../../../styles/Part3.css";

const ConversationQuestion = ({
  conversation,
  onComplete,
  onNext,
  onBack,
  onPrevious,
  hasNext,
  studyMode = "both",
  accentType = "us",
  currentIndex,
  totalCount,
}) => {
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showScript, setShowScript] = useState(false);
  const [showExplanations, setShowExplanations] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showAnswers, setShowAnswers] = useState(false);
  const [version, setVersion] = useState("basic");
  const audioRef = useRef(new Audio());
  const answerTimerRef = useRef(null);
  const [hideUnmarkedOptions, setHideUnmarkedOptions] = useState(false);
  const [viewMode, setViewMode] = useState("pure"); // 'pure', 'all', 'marked'
  const speedOptions = [1, 1.5, 2];

  useEffect(() => {
    setSelectedAnswers({});
    setShowAnswers(false);
    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
    if (answerTimerRef.current) {
      clearTimeout(answerTimerRef.current);
    }
  }, [conversation]);

  const handleAnswerSelect = (questionId, answer) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [questionId]: answer,
    });
  };

  const handleSpeedChange = (speed) => {
    audioRef.current.playbackRate = speed;
    setPlaybackRate(speed);
  };

  const handleAudioPlay = () => {
    if (!isPlaying) {
      const audioPath = conversation.versions[version].audioUrl;
      console.log("재생 중인 오디오 파일:", audioPath);
      audioRef.current.src = audioPath;
      audioRef.current.playbackRate = playbackRate;
      audioRef.current.play();
      setIsPlaying(true);

      // 재생 시작할 때 답변과 해설 초기화
      setSelectedAnswers({});
      setShowAnswers(false);
      setShowExplanations({});

      audioRef.current.onended = () => {
        setIsPlaying(false);
      };
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
  };

  const toggleExplanation = (questionId) => {
    setShowExplanations((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const isAllAnswered = () => {
    return conversation.questions.every((q) => selectedAnswers[q.id]);
  };

  const renderHTML = (text) => {
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  };

  const renderOptionText = (text) => {
    if (viewMode === "all") {
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    } else if (viewMode === "marked") {
      const matches = text.match(/<mark>.*?<\/mark>/g);
      if (!matches) return "";
      return (
        <span
          dangerouslySetInnerHTML={{
            __html: matches.join(" "),
          }}
          className="marked-text-only"
        />
      );
    } else if (viewMode === "pure") {
      return (
        <span
          dangerouslySetInnerHTML={{
            __html: text.replace(/<\/?mark>/g, ""),
          }}
        />
      );
    }
  };

  const checkAllAnswersSelected = (answers) => {
    return Object.keys(answers).length === 3;
  };

  return (
    <div className="conversation-question dark-mode">
      <div className="conversation-header">
        <button className="back-button" onClick={onBack}>
          ← 카테고리로 돌아가기
        </button>
        <div className="part3-conversation-info">
          <span className="conversation-number">
            대화 {currentIndex + 1}/{totalCount}
          </span>
          <h3>{conversation.title}</h3>
        </div>

        <div className="control-buttons">
          <button
            className={`control-btn play-btn ${isPlaying ? "playing" : ""}`}
            onClick={handleAudioPlay}
          >
            <img
              src={`/icons/${isPlaying ? "stop" : "play"}.svg`}
              alt={isPlaying ? "정지" : "재생"}
              className="play-icon"
            />
            <span className="play-text">{isPlaying ? "정지" : "재생"}</span>
          </button>

          <div className="speed-buttons">
            {speedOptions.map((speed) => (
              <button
                key={speed}
                className={`control-btn speed-btn ${
                  playbackRate === speed ? "active" : ""
                }`}
                onClick={() => handleSpeedChange(speed)}
              >
                x{speed}
              </button>
            ))}
          </div>

          <div className="view-mode-buttons">
            <button
              className={`control-btn view-mode-btn ${
                viewMode === "pure" ? "active" : ""
              }`}
              onClick={() => setViewMode("pure")}
            >
              순수
            </button>
            <button
              className={`control-btn view-mode-btn ${
                viewMode === "all" ? "active" : ""
              }`}
              onClick={() => setViewMode("all")}
            >
              전체
            </button>
            <button
              className={`control-btn view-mode-btn ${
                viewMode === "marked" ? "active" : ""
              }`}
              onClick={() => setViewMode("marked")}
            >
              마크
            </button>
          </div>
        </div>
      </div>

      <div className="conversation-content">
        <div className="questions-section">
          {conversation.questions.map((question) => (
            <div key={question.id} className="question">
              <h4>{renderHTML(question.question)}</h4>
              <div className="options">
                {Object.entries(question.options).map(([key, value]) => (
                  <label key={key} className="option-label">
                    <input
                      type="radio"
                      name={`question-${question.id}`}
                      value={key}
                      checked={selectedAnswers[question.id] === key}
                      onChange={() => handleAnswerSelect(question.id, key)}
                    />
                    {key}: {renderOptionText(value)}
                  </label>
                ))}
              </div>
              {showAnswers && (
                <div className="answer-explanation">
                  <p
                    className={
                      selectedAnswers[question.id] === question.correctAnswer
                        ? "correct-answer"
                        : "wrong-answer"
                    }
                  >
                    {selectedAnswers[question.id] === question.correctAnswer
                      ? `정답: ${question.correctAnswer}`
                      : `오답입니다. 정답은 ${question.correctAnswer}입니다.`}
                  </p>
                  <div className="explanation">
                    {(question.explanation
                      ? question.explanation.split("❌ 오답 트랩:")
                      : []
                    ).map((section, index) => {
                      if (index === 0) {
                        return (
                          <p key="answer" className="answer-section">
                            {section}
                          </p>
                        );
                      } else {
                        // 오답 트랩 부분
                        return (
                          <p key="wrong" className="wrong-section">
                            <span className="wrong-title">❌ 오답 트랩:</span>
                            {section}
                          </p>
                        );
                      }
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* 답 확인하기 버튼 */}
          {Object.keys(selectedAnswers).length === 3 && !showAnswers && (
            <button
              className="show-answer-btn"
              onClick={() => setShowAnswers(true)}
            >
              답 확인하기
            </button>
          )}

          {/* 이전/다음 버튼 */}
          <div className="button-container">
            <button
              className="nav-button prev-button"
              onClick={onPrevious}
              disabled={currentIndex === 0}
            >
              이전
            </button>
            {currentIndex < totalCount - 1 ? (
              <button className="nav-button next-button" onClick={onNext}>
                다음
              </button>
            ) : (
              <button className="complete-button" onClick={onComplete}>
                학습 완료
              </button>
            )}
          </div>
        </div>

        {(studyMode === "text" || studyMode === "both") && (
          <div className="conversation-script">
            <button
              className="toggle-script"
              onClick={() => setShowScript(!showScript)}
            >
              스크립트 {showScript ? "숨기기" : "보기"}
            </button>
            {showScript && (
              <div className="script-content">
                <div className="english-script">
                  <pre>{renderHTML(conversation.versions[version].script)}</pre>
                </div>
                <div className="korean-translation">
                  <pre>{conversation.versions[version].translation}</pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <audio ref={audioRef} onEnded={handleAudioEnd} />
    </div>
  );
};

export default ConversationQuestion;
